/* @flow */

import * as React from 'react';

import { Button, ButtonIcon, Footer, Page, PageContainer, genRuby } from '@sonyged/ext-proc';

import { type FinishedRound, Round } from './domain/Round';
import { type Ingredient, type Topping, ingredientName } from './domain/pizza';
import AutoScaleContainer from './AutoScaleContainer';
import PizzaContainer from './PizzaContainer';
import PizzaCustomerIcon from './icons/PizzaCustomerIcon';
import PizzaResearchResultView from './PizzaResearchResultView';
import RadioButton from '../../common/RadioButton';

type Props = {
  onNext: FinishedRound => void,
  onBack: () => void,
  round: FinishedRound,
  pastRound: FinishedRound,
};

type State = {
  retryRound: 'past' | 'current',
};

class PizzaResearchChooseBase extends React.Component<Props, State> {
  state = { retryRound: 'past' };

  handleSettingClick = () => {
    if (this.props.pastRound) {
      const round = this.state.retryRound === 'current' ? this.props.round : this.props.pastRound;
      this.props.onNext(round);
    } else {
      this.props.onNext(this.props.round);
    }
  };

  handleRoundSelected = (retryRound: 'past' | 'current') => {
    this.setState({ retryRound });
  };

  renderIngredients(past: Topping[], current: Topping[]): React.Node {
    let diff = false;
    if (past.length !== current.length) {
      diff = true;
    } else {
      past.forEach(function(p, i) {
        if (p !== current[i]) {
          diff = true;
        }
      });
    }
    return current.map((ingredient, i) => (
      <div key={i} className={`${diff ? 'primary20-bg-color' : ''}`}>
        {ingredientName(ingredient)}
      </div>
    ));
  }

  renderIngredient(past: Ingredient, current: Ingredient, key: string) {
    return (
      <div key={key} className={`${past === current ? '' : 'primary20-bg-color'}`}>
        {ingredientName(current)}
      </div>
    );
  }

  render() {
    const { round, pastRound } = this.props;
    const customers = round.customers;

    const pastRoundSale = Round.sale(pastRound);
    const currentRoundSale = Round.sale(round);

    let saleClass = '';
    let diff = PizzaResearchResultView.EQUALS;
    if (pastRoundSale === currentRoundSale) {
      diff = PizzaResearchResultView.EQUALS;
      saleClass = 'equals compare';
    } else if (pastRoundSale < currentRoundSale) {
      diff = PizzaResearchResultView.GREATER;
      saleClass = 'greater compare';
    } else {
      diff = PizzaResearchResultView.LESS;
      saleClass = 'lesser compare';
    }

    return (
      <div className={`pizza-inner-view pizza-result-view white-bg-color`}>
        <Page.fluid className={`gray5-bg-color research pizza-customerInfo`} fullHeight={false}>
          <PageContainer.small className="pizza-customerInfo-container">
            <div className="pizza-customers">
              <PizzaCustomerIcon talking={true} onRequestToTalk={() => {}} customer={customers[0]} />
            </div>
          </PageContainer.small>
        </Page.fluid>

        <Page.fluid menu={false} fullHeight={false} className="gray10-bg-color research flex1">
          <PageContainer.normal style={{ height: 'auto' }}>
            <table className="proc-table">
              <thead>
                <tr>
                  <th colSpan={2} className={'left-aligned'}>
                    しらべた けっか
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="proc-result-sale-info">
                    <div>
                      <span className="secondary100-color proc-small-font">まえの けっか</span>
                    </div>
                    <div className={`gray100-color mb equal compare`}>
                      <span className="proc-huge-font">{pastRoundSale}</span>
                      &nbsp;
                      <span className="proc-medium-font">円</span>
                    </div>
                  </td>
                  <td className="proc-result-sale-info">
                    <div>
                      <span className="secondary100-color proc-small-font">{genRuby('[今回|こんかい]の けっか')}</span>
                    </div>
                    <div className={`gray100-color mb ${saleClass}`}>
                      <span className="proc-huge-font">{currentRoundSale}</span>
                      &nbsp;
                      <span className="proc-medium-font">円</span>
                      {diff === PizzaResearchResultView.GREATER && <i className={'icon-proc-arrow-up'} />}
                      {diff === PizzaResearchResultView.LESS && <i className={'icon-proc-arrow-down'} />}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td
                    className={`${
                      this.state.retryRound === 'past'
                        ? 'radio-selected primary100-border-color primary20-bg-color'
                        : ''
                    }`}
                  >
                    <div className="proc-result-sale proc-result-sale--compare">
                      <div className="proc-result-sale-pizza">
                        <AutoScaleContainer
                          preferedHeight={600}
                          preferedWidth={1080}
                          preferedMinWidth={1080}
                          preferedMinHeight={600}
                        >
                          <PizzaContainer pizza={pastRound.pizza} />
                        </AutoScaleContainer>
                      </div>
                      <div className="proc-result-sale-info">
                        <div className="proc-small-font gray100-color">
                          {[pastRound.pizza.crust, pastRound.pizza.sauce]
                            .concat(pastRound.pizza.toppings)
                            .map((ingredient, i) => (
                              <div key={i}>{ingredientName(ingredient)}</div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td
                    className={`${
                      this.state.retryRound === 'current'
                        ? 'radio-selected primary100-border-color primary20-bg-color'
                        : ''
                    }`}
                  >
                    <div className="proc-result-sale proc-result-sale--compare">
                      <div className="proc-result-sale-pizza">
                        <AutoScaleContainer
                          preferedHeight={600}
                          preferedWidth={1080}
                          preferedMinWidth={1080}
                          preferedMinHeight={600}
                        >
                          <PizzaContainer pizza={round.pizza} />
                        </AutoScaleContainer>
                      </div>
                      <div className="proc-result-sale-info">
                        <div className="proc-small-font gray100-color">
                          {this.renderIngredient(pastRound.pizza.crust, round.pizza.crust, 'crust')}
                          {this.renderIngredient(pastRound.pizza.sauce, round.pizza.sauce, 'sauce')}
                          {this.renderIngredients(pastRound.pizza.toppings, round.pizza.toppings)}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <RadioButton
              value={this.state.retryRound}
              values={['past', 'current']}
              onSelected={this.handleRoundSelected}
            />
          </PageContainer.normal>
        </Page.fluid>

        <Footer.nav
          className="white-bg-color"
          navItem={
            <Button.normal onClick={this.props.onBack}>
              <ButtonIcon.back />
            </Button.normal>
          }
        >
          <Button.attention className="compare" onClick={this.handleSettingClick}>
            この けっかと くらべる
          </Button.attention>
        </Footer.nav>
      </div>
    );
  }
}

export { PizzaResearchChooseBase };
